import {blue} from "@ant-design/colors";
import {InfoCircleOutlined} from "@ant-design/icons";
import {Button, MenuProps, message} from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import moment from "moment";
import React, {FC, memo, useState} from "react";
import {Link, useHistory} from "react-router-dom";
import {SentMailsResult} from "../../../../server/mailjet.types";
import {TemplateType, useSendBookingInvoicesByBookingIdsMutation} from "../../../generated/graphql";
import {URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS, URL_INVOICE_SETTLEMENT_DETAILS} from "../../../models/url";
import i18n from "../../../services/i18n";
import DeleteButton from "../../../views/DeleteButton/DeleteButton";
import MailTemplateEditor from "../../templates/MailTemplateEditor/MailTemplateEditor";
import {TableType} from "../BookingList/BookingListColumns";
import ReverseBookingModal from "../BookingList/ReverseBookingModal";
import {GET_BOOKINGS_BY_ATTENDEE_WITH_OPTIONS} from "../bookings.graphql";
import css from "./CancelSettledBookingAction.less";
import MoreMenu from "../BookingList/MoreMenu/MoreMenu";
import useHighestRole from "../../../helpers/useHighestRole";
import Roles from "../../../../shared/models/roles";
import BookingHistoryDrawer from "../../bookingHistory/BookingHistoryDrawer";
import {ERASED_ACTION_HINT} from "../../../helpers/erasedRecordMessage";

type CancelSettledBookingActionProps = {
    record: TableType;
    refetch?: () => void;
    style?: React.CSSProperties;
    moreMenuItems?: MenuProps["items"];
};

const CancelSettledBookingAction: FC<CancelSettledBookingActionProps> = ({
    record,
    refetch,
    style,
    // setShowEmailEditor,
    // showEmailEditor,
    moreMenuItems,
}) => {
    const [showEmailEditor, setShowEmailEditor] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<TableType>();

    const highestRole = useHighestRole();
    const isInstructor = highestRole === Roles.Instructor;

    // THIS NEEDS TO BE PROBABLY IN THE COMPONENT ABOVE
    const [reverseBookingModalVisible, setReverseBookingModalVisible] = useState(false);

    const history = useHistory();

    const [sendMultipleIncoiceMails] = useSendBookingInvoicesByBookingIdsMutation();

    const showEmailEditorModal = (confirmed: boolean) => {
        setShowEmailEditor(confirmed);
        setReverseBookingModalVisible(false);
    };

    const handleSelection = (record: TableType) => {
        setSelectedRecord(record);
        setReverseBookingModalVisible(true);
    };

    const attendeeId = record?.attendee?.id;

    const refetchQueries = async () => [
        "GetBookingsOverviewOptimized",
        "GetUnsettledBookings",
        "GetSettledBookings",
        "GetBookingsByAttendeeIdWithOptions",
        {
            query: GET_BOOKINGS_BY_ATTENDEE_WITH_OPTIONS,
            skip: !record.courseId,
            // variables: {id: record.courseId},
            variables: {id: attendeeId},
        },
    ];

    const handleSendingMultipleMails = async (send: boolean) => {
        if (send) {
            if (!selectedRecord?.id) throw Error("Missing booking Ids when sending mails");
            // setSendingMessageState("loading");
            try {
                await sendMultipleIncoiceMails({
                    variables: {
                        bookingIds: [selectedRecord.id],
                        reversal: true,
                    },
                    refetchQueries: await refetchQueries(),
                }).then((res) => {
                    // console.log("sendTemplateMail res::: ", res);
                    const sentMailResult: SentMailsResult = res.data?.sendBookingInvoicesByBookingIds;

                    if (sentMailResult) {
                        const mailCount = sentMailResult.sentMailCount;
                        const emailText = mailCount === 1 ? "E-Mail" : "E-Mails";
                        const receivers = sentMailResult.sentMessages.map((message) => message.To[0].Email);
                        const receiverString = receivers.join(", ");

                        message.success({
                            key: "sendMail",
                            content: `${mailCount} ${emailText} erfolgreich versendet. Empfänger: ${receiverString}`,
                        });
                    }
                });
            } catch (error) {
                // setSendingMessageState("error");
                throw new Error(`Error useSendBookingInvoiceMutation: ${error}`);
            }
        }
        setSelectedRecord(undefined);
        setShowEmailEditor(false);
        refetch?.();
    };

    const isCanceled = record.canceled !== null;
    // Nach DSGVO-Löschantrag lehnt der Server das Storno ab (kein neuer Beleg mit den Daten der Person).
    const attendeeErased = Boolean(record.attendee?.erasedAt);
    // Sichtbarkeitsregel „Rechnungen“ (#709): Beleg verborgen → kein Storno (es braucht die Rechnung und erzeugt einen Stornobeleg)
    const invoiceHidden = !record.invoice;
    // const isSettled = isDefined(record.invoice) === true;
    const cancelDate = moment(record.canceled).format("DD.MM.YYYY");
    const detailsLink = isInstructor ? URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS : URL_INVOICE_SETTLEMENT_DETAILS;
    const detailsUrl = detailsLink ? detailsLink.replace(":id", record.id) : undefined;
    const detailsTestId = "SettledBookingsDetail";
    const detailsState = record;

    return (
        <>
            {selectedRecord && (
                <>
                    <ReverseBookingModal
                        open={reverseBookingModalVisible}
                        // closeModal={() => setReverseBookingModalVisible(false)}
                        closeModal={() => setReverseBookingModalVisible(false)}
                        confirmAction={(confirmed) => {
                            showEmailEditorModal(confirmed);
                        }}
                        record={selectedRecord}
                        refetch={refetch}
                    />
                    {/* )} */}
                    {/* THIS GETS RERENDERED A FAR TO OFTEN */}
                    {/* <MailTemplateEditor
                        asModal
                        open={showEmailEditor}
                        setVisibility={(e: boolean) => setShowEmailEditor(e)}
                        templateType={TemplateType.SettledBookingCancellation}
                        useCase="SENDMAIL"
                        confirmAction={async (e) =>
                            handleSendingMultipleMails(e)
                        }
                        receivers={[
                            selectedRecord?.attendee?.booker?.email ?? "",
                        ]}
                    /> */}
                </>
            )}

            <ButtonGroup style={style}>
                {detailsUrl === "/" ? null : (
                    <Button
                        data-testid={detailsTestId}
                        size="small"
                        // style={style}
                        style={{
                            borderColor: blue[5],
                        }}
                    >
                        <Link
                            to={{
                                pathname: detailsUrl,
                                state: {
                                    ...detailsState,
                                    returnTo: history.location.pathname + history.location.search,
                                },
                            }}
                        >
                            <InfoCircleOutlined
                                style={{
                                    color: blue[5],
                                }}
                            />
                        </Link>
                    </Button>
                )}
                <DeleteButton
                    size="small"
                    hideIcon={true}
                    data-testid="ReverseBookingButton"
                    disableConfirm
                    disabled={isCanceled || attendeeErased || invoiceHidden}
                    disabledText={
                        isCanceled
                            ? i18n.containers.bookings.BookingList.bookingReversal.disabledText({cancelDate})
                            : attendeeErased
                            ? ERASED_ACTION_HINT
                            : i18n.containers.settings.StaffVisibilityRules.reversalInvoiceHidden()
                    }
                    onClickCapture={() => handleSelection(record)}
                    style={style}
                >
                    {i18n.containers.bookings.BookingList.reverseBooking()}
                </DeleteButton>
                <BookingHistoryDrawer bookingId={detailsState.id} buttonProps={{size: "small"}} buttonStyle={{border: `1px solid ${blue[5]}`, color: blue[5], fontSize: "1.05em"}} />
                {/* !!: eine leere Liste würde sonst als „0“ gerendert */}
                {!!moreMenuItems?.length && <MoreMenu moreMenuItems={moreMenuItems} />}
            </ButtonGroup>
        </>
    );
};

export default memo(CancelSettledBookingAction);
